// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useTableRowStatus.tsx
 * @input React, StyleX, Icon, Table types
 * @output Exports useTableRowStatus hook + config type
 * @position Row-status plugin; consumed by Table via plugins prop
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Table/index.ts (exports)
 */
import { useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Icon } from "../../../Icon/index.js";
import { Tooltip } from "../../../Tooltip/index.js";

/**
 * Semantic status colors, resolved to the design system's icon color tokens.
 * Prefer these over raw CSS so status colors stay consistent with the theme.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const SEMANTIC_COLORS = {
  accent: 'var(--color-icon-accent)',
  success: 'var(--color-icon-green)',
  error: 'var(--color-icon-red)',
  warning: 'var(--color-icon-orange)',
  red: 'var(--color-icon-red)',
  orange: 'var(--color-icon-orange)',
  green: 'var(--color-icon-green)',
  yellow: 'var(--color-icon-yellow)',
  blue: 'var(--color-icon-blue)',
  gray: 'var(--color-icon-gray)'
};

/** Icon colors that map cleanly from a semantic status color. */
const ICON_COLOR_BY_STATUS = {
  accent: 'accent',
  success: 'success',
  error: 'error',
  warning: 'warning',
  red: 'red',
  orange: 'warning',
  green: 'green',
  yellow: 'warning',
  blue: 'blue',
  gray: 'gray'
};

/**
 * A row's status indicator. `color` accepts a semantic status color
 * (mapped to a theme token) or any raw CSS color string as an escape hatch.
 * By default the plugin renders a colored status dot. Provide `icon` to signal
 * status by shape as well as color, which is more accessible when several
 * statuses coexist. `label` is required so the status is never conveyed by
 * color alone — it names the indicator for assistive technology and shows on
 * hover. Return `null` for rows with no status.
 */

/** Configuration for {@link useTableRowStatus}. */

// The status column holds a small centered dot (or an icon when provided).
// A fixed narrow width keeps every row's indicator aligned in one gutter.
const STATUS_COLUMN_WIDTH = {
  type: 'pixel',
  value: 28
};

/** Resolve a semantic color name to a token, or pass a raw CSS color through. */
function resolveColor(color) {
  return SEMANTIC_COLORS[color] ?? color;
}
const _temp = {
  kzqmXN: "x1xc55vz",
  kZKoxP: "xdk7pt",
  kaIpWk: "x16rqkct",
  kmuXW: "x2lah0s",
  "$$css": true
};
const styles = {
  dot: color => [_temp, {
    kWkggS: color != null ? "xl8spv7" : color,
    $$css: true
  }, {
    "--x-backgroundColor": color != null ? color : undefined
  }]
};

/**
 * Returns a {@link TablePlugin} that prepends a narrow column signaling per-row
 * status: a colored status dot by default, or an icon when `icon` is provided
 * (shape + color is more accessible than color alone).
 *
 * @example
 * ```
 * const rowStatus = useTableRowStatus<Row>({
 *   getStatus: row =>
 *     row.state === 'error'
 *       ? {color: 'error', icon: 'error', label: 'Error'}
 *       : null,
 * });
 * <Table data={data} columns={columns} idKey="id" plugins={{rowStatus}} />;
 * ```
 */
export function useTableRowStatus(config) {
  const {
    getStatus
  } = config;
  return useMemo(() => ({
    transformColumns(columns) {
      const statusColumn = {
        key: '__rowStatus',
        header: '',
        width: STATUS_COLUMN_WIDTH,
        resizable: false,
        renderCell: item => {
          const status = getStatus(item);
          if (!status) {
            return null;
          }
          const signifier = status.icon ? /*#__PURE__*/_jsx(Icon, {
            icon: status.icon,
            size: "xsm",
            color: ICON_COLOR_BY_STATUS[status.color] ?? 'primary'
          }) : /*#__PURE__*/_jsx("span", {
            ...stylex.props(styles.dot(resolveColor(status.color)))
          });
          return /*#__PURE__*/_jsx(Tooltip, {
            content: status.label,
            children: /*#__PURE__*/_jsx("span", {
              ...{
                className: "x78zum5 x6s0dn4 xl56j7k"
              },
              role: "img",
              "aria-label": status.label,
              children: signifier
            })
          });
        }
      };
      return [statusColumn, ...columns];
    }
  }), [getStatus]);
}